import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { fromEvent, Subject, Subscription, takeUntil } from 'rxjs';
import { User, Module, SignInSignOut, SignInInfo, SignInSignOutDocument, SignInSignOutVisitReason } from 'src/app/models/user.model';
import { AuthService } from 'src/app/services/auth/auth.service';
import { NavigationService } from 'src/app/services/navigation/navigation.service';
import { SiteService } from 'src/app/services/site/site.service';
import { UserService } from 'src/app/services/user/user.service';
import { LocalizationService } from '../internationalization/localization.service';
import { CityWorkType, CompanyType, FastLinkType, JobType, PermitStatus } from 'src/app/shared/enums';
import { JobService } from 'src/app/services/job/job.service';
import { ClosedownService } from '../../services/closedown/closedown.service';
import { FormService } from 'src/app/services/form/form.service';
import { InductionConfig } from 'src/app/models/inductionconfig.model';
import { Site } from 'src/app/models/site.model';
import { NotificationPushService } from 'src/app/services/notification/notification.push.service';
import { UtilsService } from 'src/app/services/util/util-service';
import { ComplianceCheckAnswerModel } from '@app/models/inductionqna.model';
import { SiteVisitReason } from '@app/models/sitevisitreason.model';
import { LoaderService } from '@app/services/loader/loader.service';
import { Browser } from '@capacitor/browser';
import { GeolocationService } from '@app/services/geolocation/geolocation.service';
import { ConfigurationService } from '@app/services/configuration/configuration.service';
import { PermitService } from '@app/services/permit/permit-service';
import { Job, UnallocateJob } from '@app/models/job.model';
import { VisitSummaryJob } from '@app/models/visitsummaryjob.model';
import * as L from 'leaflet';
import { OpenmapService } from '@app/services/openmap/openmap.service';
import { Position } from '@capacitor/geolocation';
import { ReferenceService } from '@app/services/reference/reference.service';
import { VisitorTypeUploadReason } from '@app/models/visitortypeuploadreason.model';

@Component({
    selector: 'app-site-induction-completed',
    templateUrl: './site-induction-completed.component.html',
    styleUrls: ['./site-induction-completed.component.scss'],
    standalone: false
})
export class SiteInductionCompletedComponent implements OnInit, OnDestroy {
    private unsubscriberBackBtn: Subject<void> = new Subject<void>();
    private currentLocationSubscription: Subscription | undefined;
    induction: string = 'INDUCTION';
    nonInduction: string = 'NONINDUCTION';
    currentUser: User | undefined;
    fullname: string | undefined;
    bearerToken: string | undefined;
    allModules: Module[] = [];
    distinctModules: Module[] = [];
    inductionConfig: InductionConfig | undefined;
    selectedSite: Site | undefined;
    permit: string[] = [];
    permitStatus: number = PermitStatus.DISABLED;
    permitStatusEnum = PermitStatus;
    currentPermitId: string = '';
    signIn: boolean = false;
    signInExistsMatchString: string = 'Sign In info already exists';
    outOfRange: boolean = false;
    siteVisitReasons: SiteVisitReason[] = [];
    showWorkBench: boolean = false;
    showMyJobs: boolean = false;
    showProductLabel: boolean = false;
    allocatedJobs: VisitSummaryJob[] = [];
    userCompanies: string[] = [];
    nonCityFMWork: string = CityWorkType.NONCITYWORK;
    disableSignOut: boolean = false;
    isDebugModeActive: boolean = true;
    addPhotosLabel: string = this._localizationService.translate("siteinductioncompleted_btn_add_photos");
    signInSignOutVisitReasons: SignInSignOutVisitReason[] = [];
    signInSignOutDocuments: SignInSignOutDocument[] = [];
    visitorTypeUploadReasons : VisitorTypeUploadReason[] = [];

    private map: L.Map | undefined;
    @ViewChild('mapContainer', { static: false }) mapContainer!: ElementRef;

    constructor(private _authService: AuthService,
        private _siteService: SiteService,
        public _router: Router,
        private _userService: UserService,
        private _navigationService: NavigationService,
        private _localizationService: LocalizationService,
        private _closedownService: ClosedownService,
        private _formService: FormService,
        private _jobService: JobService,
        private _notificationPushService: NotificationPushService,
        private _utilsService: UtilsService,
        private _loaderService: LoaderService,
        private _geolocationService: GeolocationService,
        private _configurationService: ConfigurationService,
        private _permitService: PermitService,
        private _openMapService: OpenmapService,
        private _referenceService: ReferenceService
    ) {
        this._navigationService.onChange(true, false);
        this.initBrowserBackButton();
    }

    ngOnInit(): void { 
        this.getUserDetails();
        this.initRedirection();
    }

    private async getUserDetails(){
        this._userService.getUserByEmailWithDetails()
            .subscribe({
            next: (user) => {
                if(user){
                    this.isDebugModeActive = user.isDebugModeActive;
                }
            },
            error: () => {}
            });
    }

    async initMap() {
        if(this.selectedSite){
            const mapId = this.mapContainer.nativeElement.id || 'map';

            this.map = await this._openMapService.initMap(mapId, 
                this._geolocationService.getLatestLocation()?.coords.latitude || 0, 
                this._geolocationService.getLatestLocation()?.coords.longitude || 0);

            var contents = '<b>'  + this.selectedSite.name + '</b>';
            this._openMapService.addMarker(
                this.selectedSite.lat, 
                this.selectedSite.lon, 
                "", 
                contents,
                this.selectedSite.brand?.imageUrl,
                'site'
            );
        }
       
    }

    initRedirection(): void {

        this._closedownService.getInductionFirstRedirect().then(
            async result => {
                if (result == 'true') {
                    this.signIn = true;
                }

                this.allModules = await this._authService.getUserModule();
                if (this.allModules) {
                    this.distinctModules = this.allModules.filter(s => s.isIntegrationModule == true);
                }

                this.permit = await this._configurationService.getPermitKey();

                this._authService.getTokenFromStorage().then(async bearer => {
                    await this._closedownService.setBearerToken(bearer);

                    this._authService.getUser().then(
                        async user => {
                            this.currentUser = user;
                            await this._closedownService.setCurrentUser(user);

                            this.fullname = `${user.firstName} ${user.lastName}`;
                            this.loadSite();
                        });
                });

            }
        );
    }

    loadSite(): void {
        this._siteService.getSignedInSite().then(async store => {
            this.selectedSite = store;
            var processFlow = await this._closedownService.getProcessFlow();
            processFlow.siteCode = store?.code;
            await this._closedownService.setProcessFlow(processFlow);

            if (this.signIn) {
                this.loadInductionConfig();
            }
            else {
                this.getSignInSignOutDocuments();
                this.checkPermitStatus();
                this.processSiteVisitReasons();
                if (this._geolocationService.getLatestLocation() == null) {
                    await this._geolocationService.startLocationWatch();
                }

                await this._notificationPushService.startOutOfRangeCheck(this.selectedSite);
                this.subscribeToLocationChanges();
            }

            if(this.isDebugModeActive){
                await this.initMap();
            }
        });
    }

    loadInductionConfig(): void {
        this._formService.getInductionConfig()
            .then(form => {
                if (form) {
                    this.inductionConfig = form;
                }
                this.postSignIn();
            });
    }

    async processSignInSignOutVisitReason() {
        this.signInSignOutVisitReasons = [];
        this.siteVisitReasons = await this._siteService.getVisitReasons();
        if (this.siteVisitReasons && this.siteVisitReasons.length > 0) {
           this.siteVisitReasons.forEach(e => {
              if (e.visitorTypeId && e.visitReasonId) {
                var signInSignOutVisitReason: SignInSignOutVisitReason = { 
                  visitReasonId: e.visitReasonId,
                  visitReasonDescription: e.calloutReferenceNumber,
                  id: undefined,
                  signInSignOutId: undefined,
                  visitorTypeId: e.visitorTypeId
                }
                this.signInSignOutVisitReasons.push(signInSignOutVisitReason);
              }
             
           });
        }
    }

    async postSignIn() {
        if (this.currentUser && this.selectedSite) {
            await this.processSignInSignOutVisitReason();
            let visitorType = "";
            let reasonforvisit = "";
            let compliancecheck = "";
            let indcutionQnA = "";
            let userDocumentIds: string[] = [];
            if (this.inductionConfig) {
                visitorType = this.inductionConfig.visitorType;
                this.inductionConfig.inductionConfigDetails.forEach(details => {
                    switch (details.type.toLowerCase()) {
                        case "reasonforvisit": {
                            if (details.answer && details.answer.length > 0) {
                                reasonforvisit = details.answer;
                                this._siteService.setReasonForVisit(reasonforvisit, this.signInSignOutVisitReasons);
                            }
                            break;
                        }
                        case "compliancecheck": {
                            if (details.answer && details.answer.length > 0) {
                                compliancecheck = details.answer;
                            }
                            break;
                        }
                        default:
                            if (details?.userDocumentIds && details?.userDocumentIds.length > 0) {
                                userDocumentIds = [...details?.userDocumentIds];
                            }

                            break;
                    }
                });
                indcutionQnA = JSON.stringify(this.inductionConfig);
            }

            var compliaceCompany = "";
            var complianceCompanyRapidGlobalId = "";
            var inductionId = "";
            var inductionGuid = "";
            var rapidGlobalGuid = "";
            var rapidGlobalGuid = "";
            var statusId = -1;
            if (compliancecheck) {
                var objCompliance = JSON.parse(compliancecheck) as ComplianceCheckAnswerModel;
                compliaceCompany = objCompliance.companyName;
                complianceCompanyRapidGlobalId = objCompliance.rapidGlobalId;
                inductionId = objCompliance.inductionNo;
                inductionGuid = objCompliance.inductionGuid;
                rapidGlobalGuid = objCompliance.rapidGlobalGuid;
                statusId = objCompliance.statusId;
            }

            userDocumentIds = userDocumentIds.filter((item, index, self) => self.indexOf(item) === index);
            let localStorageSignInMethod = await this._userService.getSignInMethod();
            let postSignIn: SignInSignOut = {
                id: undefined,
                userId: this.currentUser.id,
                signInDateTime: new Date(),
                signOutDateTime: new Date(0),
                signInType: (this.selectedSite.isInductionRequired) ? this.induction : this.nonInduction,
                siteId: this.selectedSite.id,
                visitorType: visitorType,
                reasonForVisit: this._utilsService.encodeBase64String(reasonforvisit),
                complianceCompany: compliaceCompany,
                complianceCompanyRapidGlobalId: complianceCompanyRapidGlobalId,
                inductionId: inductionId,
                signOutSignature: "",
                inductionGuid: inductionGuid,
                companyRapidGlobalGuid: rapidGlobalGuid,
                userDocumentIds: userDocumentIds,
                complianceCompanyRapidGlobalStatus: statusId,
                signInMethod: localStorageSignInMethod,
                inductionQnA: this._utilsService.encodeBase64String(indcutionQnA),
                signInSignOutVisitReasons: this.signInSignOutVisitReasons
            }
            await this._userService.removeSignInMethod();

            let latestLocation = this._geolocationService.getLatestLocation();
            if (latestLocation && latestLocation.coords.latitude && latestLocation.coords.longitude) {
                postSignIn.SignInLatLon = latestLocation.coords.latitude + "," + latestLocation.coords.longitude;
            }

            this._loaderService.showPageLoading();
            this._userService.addSignIn(postSignIn).subscribe({
                next: async (success) => {
                    let signInInfoSetInLocal: SignInInfo = {
                        signInSignOutId: success.id,
                        siteId: success.siteId,
                        complianceCompany: success.complianceCompany
                    }

                    await this._closedownService.removeInductionFirstRedirect();
                    await this._userService.setSignIn(signInInfoSetInLocal);

                    const permitQnA = await this._formService.getPermitQnA();
                    await this._formService.removePermitQnA();

                    this._formService.removeInductionConfig()
                        .then(async _ => {
                            if (this.selectedSite) {
                                await this._notificationPushService.startOutOfRangeCheck(this.selectedSite);
                                this.subscribeToLocationChanges();
                            }

                            await this.processSiteVisitReasons();
                            if (permitQnA && permitQnA.answer.includes(this.permit[1])) {
                                this._router.navigate(['openpermit']);
                            } else {
                                this.checkPermitStatus();
                            }

                            this._loaderService.hidePageLoading();
                        });
                },
                error: async (reject) => {
                    this._loaderService.hidePageLoading();
                    await this._closedownService.removeInductionFirstRedirect();
                    await this._formService.removePermitQnA();
                    await this._formService.removeInductionConfig();
                    if (reject.error) {
                        var errorMessage = reject.error;
                        if (errorMessage.toLowerCase().indexOf(this.signInExistsMatchString.toLowerCase()) > -1) {
                            alert(this._localizationService.translate("siteinductioncompleted_error_sign_in_info_exist"));
                            let signInInfoSetInLocal: SignInInfo = {
                                signInSignOutId: this.currentUser?.id,
                                siteId: "",
                                complianceCompany: ""
                            }
                            await this._userService.setSignIn(signInInfoSetInLocal);
                            await this.processSiteVisitReasons();
                            this.checkPermitStatus();
                        }
                        else {
                            if (reject.error.errorCode) {
                                alert(this._localizationService.translate(reject.error.errorCode));
                            }
                            else {
                                alert(this._localizationService.translate('siteinductioncompleted_error_save_sign_in_info'));
                            }
                        }
                    }
                    else {
                        alert(this._localizationService.translate("siteinductioncompleted_error_save_sign_in_info"));
                        this._router.navigate(['signin']);
                    }
                }
            })
        }
    }

    async updateReasonForVisit(jobNumber: string, newReasonForVisit: string) {
        this._userService.getSignIn().then(async signInInfo => {
            const site = await this._siteService.getSignedInSite();

            const emptyAnswerString = "";
            if (this.currentUser) {
                let postSignIn: SignInSignOut = {
                    id: signInInfo.signInSignOutId,
                    userId: this.currentUser.id,
                    signInDateTime: new Date(),
                    signOutDateTime: new Date(0),
                    signInType: "",
                    siteId: site.id,
                    visitorType: "",
                    reasonForVisit: this._utilsService.encodeBase64String(newReasonForVisit),
                    complianceCompany: "",
                    complianceCompanyRapidGlobalId: "",
                    inductionId: "",
                    signOutSignature: "",
                    inductionGuid: "",
                    companyRapidGlobalGuid: "",
                    userDocumentIds: [],
                    complianceCompanyRapidGlobalStatus: 0,
                    signInMethod: "",
                    inductionQnA: this._utilsService.encodeBase64String(""),
                    signInSignOutVisitReasons: this.signInSignOutVisitReasons
                };

                this._userService.updateReasonForVisit(postSignIn).subscribe({
                    error: error => {
                        if (error.error && error.error.errorCode) {
                            alert(this._localizationService.translate(error.error.errorCode));
                        }
                        else {
                            alert(this._localizationService.translate("siteinductioncompleted_unallocate_job_update_reasonforvisit_error"));
                        }
                    }
                })
            }
        });
    }

    reloadWhenBrowserClose() {
        if (this._utilsService.isMobileAndTablet()) {
            Browser.addListener('browserFinished', () => {
                Browser.removeAllListeners().then(s => { });
                this.reloadPage();
            });
        }
    }

    reloadPage() {
        window.location.reload();
    }

    siteSignOutOnClick() {
        let allowedToSignOut: boolean = true;
        if (this.visitorTypeUploadReasons && this.visitorTypeUploadReasons.length > 0) {
            const requiredUploadReasons = this.visitorTypeUploadReasons.filter(ur => ur.isRequired);
            if (requiredUploadReasons && requiredUploadReasons.length > 0) {
                if (this.signInSignOutDocuments && this.signInSignOutDocuments.length > 0) {
                    requiredUploadReasons.forEach(rur => {
                        const found = this.signInSignOutDocuments.find(doc => doc.visitorTypeUploadReasonId === rur.id);
                        if (!found) {
                            allowedToSignOut = false;
                        }
                    });
                }
                else {
                    allowedToSignOut = false;
                }
            }
        }
        if (allowedToSignOut) {
            this._router.navigate(['sitesignout']);
        }
        else {
            alert(this._localizationService.translate('siteinductioncompleted_mandatory_uploadreason_photo_required'));
        }
    }

    openPermitOnClick() {
        if (this.permitStatus == PermitStatus.NEW) {
            if (confirm(this._localizationService.translate("siteinductioncompleted_confirm_create_permit"))) {
                this._router.navigate(['openpermit']);
            }
        } else if (this.permitStatus == PermitStatus.DRAFT) {
            this._router.navigate(['openpermit']);
        }
    }

    cancelDraftPermitOnClick() {
        if (confirm(this._localizationService.translate("siteinductioncompleted_cancel_draft_permit_confirmation"))) {
            this._permitService.cancelDraftPermit(this.currentPermitId).subscribe({
                next : result => {
                    if(result){
                        this.currentPermitId = '';
                        this.permitStatus = PermitStatus.NEW;
                    }
                }
                , error: (error) => {
                    if (error.error && error.error.errorCode) {
                        alert(this._localizationService.translate(error.error.errorCode));
                    }
                    else {
                        alert(this._localizationService.translate("siteinductioncompleted_error_cancel_draft_permit"));
                    }
                    console.error(this._localizationService.translate('siteinductioncompleted_error_cancel_draft_permit', error));
                }
            })
        }
    }

    permitWelfareOnClick() {
        if (this.permitStatus == PermitStatus.SUMBITTED) {
            this._router.navigate(['permitwelfarechecks']);
        }
    }

    closePermitOnClick() {
        if (this.permitStatus == PermitStatus.SUMBITTED) {
            this._router.navigate(['closepermit']);
        }
    }

    unallocateJobOnClick(selectedJob: VisitSummaryJob) {
        if (confirm(this._localizationService.translate("siteinductioncompleted_unallocate_job_confirmation"))) {

            var unallocatedJob: UnallocateJob = {
                jobId: selectedJob.job.referenceNumber,
                resourceId: selectedJob.job.companyReferenceNumber,
                calloutReferenceNumber: selectedJob.job.calloutReferenceNumber,
                jobType: selectedJob.job.jobType
            }

            this._jobService.unallocateJob(unallocatedJob).subscribe(
                {
                    next: result => {
                        if (result) {
                            // Update siteVisitReasons local variable
                            const reason = this.siteVisitReasons.find(r => r.id === selectedJob.job.referenceNumber);
                            if (reason) {
                                reason.isUnallocated = true;
                            }
                            // Update local storage
                            this._siteService.setReasonForVisit(JSON.stringify(this.siteVisitReasons), this.signInSignOutVisitReasons);
                            // Update allocatedJobs local variable
                            const unallocatedJob = this.allocatedJobs.find(j => j.job.referenceNumber === selectedJob.job.referenceNumber);
                            if (unallocatedJob) {
                                unallocatedJob.isUnallocated = true;
                            }
                            // Update DB
                            this.updateReasonForVisit(selectedJob.job.id, JSON.stringify(this.siteVisitReasons));

                            this.updateDisableSignOut(this.allocatedJobs);
                        }
                    },
                    error: error => {
                        if (error.error && error.error.errorCode) {
                            alert(this._localizationService.translate(error.error.errorCode));
                        }
                        else {
                            alert(this._localizationService.translate("siteinductioncompleted_unallocate_job_error"));
                        }
                    }
                })
        }
    }

    checkPermitStatus() {
        if (this.selectedSite?.isPermitRequired) {
            this._userService.getSignIn().then(signInInfo => {
                if (signInInfo !== undefined && signInInfo.signInSignOutId !== "") {
                    this._permitService.getPermitBySignInSignOutId(signInInfo.signInSignOutId).subscribe({
                        next: (permit) => {
                            if (permit) {
                                this.permitStatus = permit.status;
                                this.currentPermitId = permit.id ? permit.id : '';
                            } else {
                                this.permitStatus = PermitStatus.NEW;
                            }
                        }, error: (error) => {
                            if (error.error && error.error.errorCode) {
                                alert(this._localizationService.translate(error.error.errorCode));
                            }
                            else {
                                alert(this._localizationService.translate("close_permit_fetching_permit_error"));
                            }
                            console.error(this._localizationService.translate('close_permit_fetching_permit_error', error));
                        }
                    });
                }
                else {
                    alert(this._localizationService.translate('open_permit_no_signin_found'));
                    this._router.navigate(['signin']);
                }
            });
        } else {
            this.permitStatus = PermitStatus.NA;
        }
    }

    async processSiteVisitReasons() {
        this.siteVisitReasons = await this._siteService.getVisitReasons();
        await this.getUploadReasons();
        if (this.siteVisitReasons && this.siteVisitReasons.length > 0) {
            if (this.currentUser) {
                if (this.currentUser.companies) {
                    this.currentUser.companies.forEach(company => {
                        if (company.companyType.toUpperCase() != CompanyType.SUBCON) {
                            this.userCompanies.push(company.referenceNumber);
                        }
                    });
                }

                if (this.siteVisitReasons[0].id !== '') {
                    this.getJobsByReasons();
                } else {
                    this.checkNonCityJobsDisplayValue(this.siteVisitReasons);
                }
            }
        }
        else {
            this.showWorkBench = false;
        }
    }

    private checkNonCityJobsDisplayValue(reasons: SiteVisitReason[]) {
        this.showWorkBench = false;
        reasons.forEach(reason => {
            if (reason.calloutReferenceNumber === this.nonCityFMWork) {
                reason.calloutReferenceNumber = this._localizationService.translate("reasonforvisit_lbl_noncityfmwork");
            }
        });
    }

    private getJobsByReasons() {
        this._jobService.getJobsBySelectedReasons(this.siteVisitReasons.map((s) => s.id), this.selectedSite?.id!, this.siteVisitReasons.map((s) => s.calloutReferenceNumber)).subscribe({
            next: (jobs) => {
                if (jobs) {
                    this._closedownService.setUserJobs(jobs);
                    this.getAllocatedJobs(this.userCompanies, jobs);
                    this.isShowWorkBench(this.userCompanies, jobs);
                }
            },
            error: e => {
                if (e.error && e.error.errorCode) {
                    alert(this._localizationService.translate(e.error.errorCode));
                }
                else {
                    alert(this._localizationService.translate("siteinductioncompleted_error_get_job"));
                }
            }
        }
        );
    }

    private getAllocatedJobs(userCompanies: string[], jobs: Job[]) {
        jobs.forEach(job => {
            var siteVisitReason = this.siteVisitReasons.find(x => x.id === job.referenceNumber);

            var imageUrl = "warning";
            var status = "";

            if (siteVisitReason?.id == job.referenceNumber) {

                if ((siteVisitReason.calloutReferenceNumber != "") &&
                    (siteVisitReason.version != job.version && job.jobStatusName?.toLowerCase() != "callout")) {
                    imageUrl = "done";
                    status = job.jobStatusName;
                } else {
                    imageUrl = "warning";
                    status = "Not Updated";
                }

                if ((job.allocatedUserEmail) || (userCompanies?.some(x => x == job.companyReferenceNumber))) {

                    var vsj: VisitSummaryJob = {
                        job: job,
                        imageUrl: imageUrl,
                        description: "",
                        resourceId: job.companyReferenceNumber,
                        jobStatus: status,
                        updated: imageUrl == "done" ? true : false,
                        fastLinkEnabled: (userCompanies?.some(x => x == job.companyReferenceNumber)) || (job.allocatedUserEmail?.toLowerCase() == this.currentUser?.emailAddress.toLowerCase()),
                        isUnallocated: siteVisitReason.isUnallocated
                    }

                    this.allocatedJobs.push(vsj);
                }
            }
        })
        this.updateDisableSignOut(this.allocatedJobs);
    }

    public enableUnallocated(job: VisitSummaryJob): boolean {
        return this.currentUser?.companies?.find(x => x.referenceNumber == job.resourceId)?.companyType.toUpperCase() == CompanyType.PARENT
            && !job.updated
            && !job.isUnallocated;
    }

    public filterAllocatedJobs(jobs: VisitSummaryJob[]): VisitSummaryJob[] {
        return jobs.filter(job => !job.isUnallocated);
    }

    private updateDisableSignOut(jobs: VisitSummaryJob[]) {
        let isAnyJobNotUpdated = jobs.some(job => job.jobStatus === "Not Updated" && !job.isUnallocated);
        let isJobStatusCheckRequired = false;

        if (this.selectedSite && this.selectedSite.isJobStatusCheckRequired != null) {
            isJobStatusCheckRequired = this.selectedSite.isJobStatusCheckRequired;
        }

        this.disableSignOut = isJobStatusCheckRequired && isAnyJobNotUpdated;
    }

    private isShowWorkBench(userCompanies: string[], jobs: Job[]) {
        for (const job of jobs) {
            if ((userCompanies?.some(x => x == job.companyReferenceNumber)) || (job.allocatedUserEmail?.toLowerCase() == this.currentUser?.emailAddress.toLowerCase())) {
                this.showWorkBench = true;
                return;
            }
        }
    }

    async moduleOnFirstRedirect(module: Module) {

        switch (module.productName.toUpperCase()) {
            case this._closedownService.workBench:
                this.siteVisitReasons = await this._siteService.getVisitReasons();
                if (this._closedownService.IsClosedownRedirectPermitted() && this.siteVisitReasons && this.siteVisitReasons.length > 0) {
                    if (this.siteVisitReasons[0].id != "") {
                        this.showWorkBench = true;
                        if (this._utilsService.isMobileDevice()) {
                            this._closedownService.directToClosedown(false);
                            this.reloadWhenBrowserClose();
                        } else {
                            this._closedownService.directToClosedown(false);
                        }
                    }
                }
                break;
            default:
                this._closedownService.appPlatformRedirect(module);
                break;
        }
        this._loaderService.hidePageLoading();
    }

    moduleOnClick(module: Module) {

        switch (module.productName.toUpperCase()) {
            case this._closedownService.workBench:
                this.goToWorkBench();
                break;
            default:
                this._closedownService.appPlatformRedirect(module);
                break;
        }

    }

    goToWorkBench() {
        let userCompanies: string[] = [];
        if (this.currentUser) {
            if (this.currentUser.companies) {
                this.currentUser.companies.forEach(company => {
                    if (company.companyType.toUpperCase() != CompanyType.SUBCON) {
                        userCompanies.push(company.referenceNumber);
                    }
                });
            }
            this._jobService.getJobsByCompanyRefNumber(undefined).subscribe({
                next: async jobs => {
                    if (jobs && jobs.length > 0 && this.currentUser) {
                        await this._closedownService.setUserJobs(jobs);
                        var processFlow = await this._closedownService.getProcessFlow();
                        if (processFlow.siteCode != '' && processFlow.token) {
                            await this._closedownService.setWithFastLink(true);
                            processFlow.linkType = FastLinkType.Closedown;
                            await this._closedownService.setProcessFlow(processFlow);
                        }
                        this._closedownService.directToClosedown(false);

                    }
                },
                error: error => {
                    if (error.error && error.error.errorCode) {
                        alert(this._localizationService.translate(error.error.errorCode));
                    }
                    else {
                        alert(this._localizationService.translate("siteinductioncompleted_error_get_job"));
                    }
                }
            })
        }
    }

    initBrowserBackButton() {
        history.pushState(null, '');
        fromEvent(window, 'popstate').pipe(takeUntil(this.unsubscriberBackBtn)).subscribe((_) => {
            history.pushState(null, '');
        });
    }

    showModule(module: Module): boolean {
        let isShow = false;

        if (module.productName.toUpperCase() == this._closedownService.workBench) {
            isShow = this.showWorkBench;
        } else {
            isShow = true;
        }

        if (isShow) {
            this.showProductLabel = true;
        }

        return isShow;

    }

    isJobReason(): boolean {
        if (this.siteVisitReasons && this.siteVisitReasons.length > 0 && this.siteVisitReasons[0].id !== '') {
            return true;
        }
        return false;
    }

    isReactiveJob(job: Job) {
        return (job.jobType == JobType.REACTIVE_JOB) ? true : false;
    }

    isPPMJob(job: Job) {
        return (job.jobType == JobType.PPM_JOB) ? true : false;
    }

    async fastlinkOnClick(resourceId: string, jobId: string, subcontractorResourceId: string, calloutReferenceId: string, jobType: string) {
        if (this.currentUser) {
            var processFlow = await this._closedownService.getProcessFlow();
            var allocatedToUser = this.allocatedJobs.some(s => s.job.allocatedUserEmail && s.job.allocatedUserEmail.toLowerCase() == this.currentUser?.emailAddress.toLowerCase())
            processFlow.resource = resourceId;
            processFlow.jobId = jobId;
            var company = this.currentUser.companies.find(c => c.referenceNumber.toLowerCase() == resourceId.toLowerCase());
            if (company || allocatedToUser) {
                if (jobType == JobType.REACTIVE_JOB) {
                    if (company?.companyType == CompanyType.SELF) {
                        processFlow.url = btoa(`/#/Job/New/${jobId}`);
                    } else {
                        processFlow.linkType = FastLinkType.Closedown;
                        processFlow.url = btoa(`/#/FastClosedown/${jobId}/${calloutReferenceId}/${resourceId}/${subcontractorResourceId}`);
                    }

                } else if (jobType == JobType.PPM_JOB) {
                    if (company?.companyType == CompanyType.SELF) {
                        processFlow.url = btoa(`/#/Job/PPM/${jobId}`);
                    } else {
                        processFlow.linkType = FastLinkType.Closedown;
                        processFlow.url = btoa(`/#/PPMJob/${jobId}/${calloutReferenceId}/0/0/${subcontractorResourceId}`);
                    }
                }

                this._closedownService.setProcessFlow(processFlow);
                this._closedownService.directToClosedown(false, resourceId);

                if (this._utilsService.isMobileDevice()) {
                    this.reloadWhenBrowserClose();
                }
            }

        }
    }

  

     private subscribeToLocationChanges(): void {
        this.currentLocationSubscription = this._geolocationService.currentLocationObservable$.subscribe(
          (position: Position | null) => {
            if (position && this.selectedSite) {
                const distanceInKilometers = this._utilsService.getDistanceBetweenTwoPointsinKM(position?.coords?.latitude,
                                         position?.coords?.longitude, this.selectedSite.lat, this.selectedSite.lon);
                const distanceInMeters = this._utilsService.convertKilometersToMeters(distanceInKilometers);
                this.outOfRange = !this._utilsService.checkSiteIsInRange(this.selectedSite, distanceInMeters);
            }
          }
        );
      }

    displayVisitorCard() {
        this._router.navigate(['visitorcard']);
    }

    ngOnDestroy(): void {
        if (this.map) {
            this.map.remove();
        }

        if (this.currentLocationSubscription) {
            this.currentLocationSubscription.unsubscribe();
        }
    }

    getSignInSignOutDocuments() {
        this._userService.getSignIn().then(signInInfo => {
            if (signInInfo !== undefined && signInInfo.signInSignOutId !== undefined && signInInfo.signInSignOutId !== "") {
                this._userService.getSignInSignOutDocuments(signInInfo.signInSignOutId).subscribe({
                    next: (documents) => {
                        if (documents && documents.length > 0) {
                            this.signInSignOutDocuments = documents;
                            this.addPhotosLabel = this._localizationService.translate("siteinductioncompleted_btn_add_photos_count").replace("{count}", `${documents.length}`);
                        } 
                    }, error: (error) => {
                        if (error.error && error.error.errorCode) {
                            alert(this._localizationService.translate(error.error.errorCode));
                        }
                        else {
                            alert(this._localizationService.translate("siteinductioncompleted_fetching_photo_error"));
                        }
                        console.error(this._localizationService.translate('siteinductioncompleted_fetching_photo_error', error));
                    }
                });
            }
            else {
                alert(this._localizationService.translate('siteinductioncompleted_no_signin_found'));
                this._router.navigate(['signin']);
            }
        });

    }

    addPhotos() {
        if (this.signInSignOutDocuments && this.signInSignOutDocuments.length > 0) {
            this._router.navigate(['photolist']);
        }
        else {
            this._router.navigate(['addnewphoto', false]);
        }
    }

    async getUploadReasons() {
        let visitorTypeId = "00000000-0000-0000-0000-000000000000";
        if (this.siteVisitReasons && this.siteVisitReasons.length > 0 &&
            this.siteVisitReasons[0].visitorTypeId && this.siteVisitReasons[0].visitorTypeId != "") {
            visitorTypeId = this.siteVisitReasons[0].visitorTypeId;
        }
        this._referenceService.getUploadReasons(visitorTypeId, true).subscribe({
            next: async (uploadReasons) => {
                if (uploadReasons && uploadReasons.length > 0) {
                    this.visitorTypeUploadReasons = uploadReasons;
                }
            },
            error: error => {
                if (error.error && error.error.errorCode) {
                    alert(this._localizationService.translate(error.error.errorCode));
                }
            }
        })
    }
}